import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Container,
  Chip,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Clock,
  CheckCircle,
  XCircle,
  CaretRight,
  CaretLeft
} from '@phosphor-icons/react';
import { useAuth } from '../../context/AuthContext';
import { useBusiness } from '../../context/BusinessContext';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import LeadCoupleAutocomplete from '../Common/LeadCoupleAutocomplete';

const SupplierManualBookingForm = ({ onBookingSuccess, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const { selectedBusiness } = useBusiness();
  const navigate = useNavigate();
  const { search } = window.location;
  
  // Get prefilled data from URL query params
  const urlParams = new URLSearchParams(search);
  const prefilledDate = urlParams.get('date');
  const prefilledTime = urlParams.get('time');
  const prefilledStaffMember = urlParams.get('staffMember');
  const businessId = selectedBusiness?.business?._id || selectedBusiness?._id || '';
  const normaliseId = (value) => {
    if (!value) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'object' && value._id) return String(value._id);
    if (value.toString) return value.toString();
    return '';
  };

  const parseLocalDateString = (dateStr) => {
    if (!dateStr) return null;
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };
  
  // Always start at service selection step - service must be selected first
  // Data from calendar clicks will be prefilled but user still needs to select service
  const getInitialStep = () => {
    return 0; // Always start at service selection
  };
  
  const [activeStep, setActiveStep] = useState(getInitialStep());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    businessId,
    serviceTypeId: '',
    startTime: '',
    endTime: '',
    clientNotes: '',
    clientName: '',
    clientEmail: '',
    clientPhone: '',
    selectedLeadId: '',
    location: {
      type: 'on_site',
      address: '',
      videoLink: ''
    }
  });

  // Available data
  const [serviceTypes, setServiceTypes] = useState([]);
  const [selectedServiceType, setSelectedServiceType] = useState(null);
  const [selectedDate, setSelectedDate] = useState(prefilledDate || '');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [availableTeamMembers, setAvailableTeamMembers] = useState([]);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [customStartTime, setCustomStartTime] = useState(prefilledTime || '09:00');
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [conflictResolutionState, setConflictResolutionState] = useState(null);
  const [replacementTeamMemberId, setReplacementTeamMemberId] = useState('');
  const [shiftPromptOpen, setShiftPromptOpen] = useState(false);
  const [shiftPromptState, setShiftPromptState] = useState(null);
  const [shiftStartTimeInput, setShiftStartTimeInput] = useState('');
  const [shiftEndTimeInput, setShiftEndTimeInput] = useState('');

  const formatTimeInputValue = (dateValue) => {
    if (!dateValue) return '';
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  const isServiceAllowedForPrefilledStaff = (serviceType) => {
    if (!prefilledStaffMember) return true;
    const allowedIds = (serviceType?.allowedTeamMembers || []).map(normaliseId).filter(Boolean);
    if (allowedIds.length === 0) return true;
    return allowedIds.includes(normaliseId(prefilledStaffMember));
  };

  // Update selectedDate and formData when URL params change
  useEffect(() => {
    if (prefilledDate) {
      setSelectedDate(prefilledDate);
    }
    if (prefilledTime) {
      setCustomStartTime(prefilledTime);
    }
    if (prefilledTime && prefilledDate) {
      // Combine date and time to create startTime
      const dateTimeStr = `${prefilledDate}T${prefilledTime}:00`;
      const startDateTime = new Date(dateTimeStr);
      
      // Set start time immediately
      setFormData(prev => ({
        ...prev,
        startTime: startDateTime.toISOString()
      }));
      
      // If we have a service type, we can calculate end time
      if (formData.serviceTypeId && selectedServiceType) {
        const duration = selectedServiceType.duration || 60; // Default to 60 minutes
        const endDateTime = new Date(startDateTime);
        endDateTime.setMinutes(endDateTime.getMinutes() + duration);
        
        setFormData(prev => ({
          ...prev,
          endTime: endDateTime.toISOString()
        }));
      }
    }
    
  }, [prefilledDate, prefilledTime, prefilledStaffMember, formData.serviceTypeId, selectedServiceType]);

  // Pre-select a team member passed from the calendar without checking their shifts.
  useEffect(() => {
    if (prefilledStaffMember && !selectedTeamMembers.includes(prefilledStaffMember)) {
      setSelectedTeamMembers([prefilledStaffMember]);
    }
  }, [prefilledStaffMember, selectedTeamMembers]);

  const steps = [
    'Select Appointment',
    'Choose Date & Time',
    'Select Team Members',
    'Client Details',
    'Confirm Booking'
  ];

  useEffect(() => {
    if (businessId) {
      setFormData(prev => ({ ...prev, businessId }));
      fetchServiceTypes();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [businessId]);

  useEffect(() => {
    if (formData.serviceTypeId) {
      const serviceType = serviceTypes.find(st => st._id === formData.serviceTypeId);
      setSelectedServiceType(serviceType);
      
      // If we have a prefilled time and date, calculate end time now that we have service type
      if (prefilledTime && prefilledDate && serviceType) {
        const dateTimeStr = `${prefilledDate}T${prefilledTime}:00`;
        const startDateTime = new Date(dateTimeStr);
        const duration = serviceType.duration || 60;
        const endDateTime = new Date(startDateTime);
        endDateTime.setMinutes(endDateTime.getMinutes() + duration);
        
        setFormData(prev => ({
          ...prev,
          endTime: endDateTime.toISOString()
        }));
      }
    }
  }, [formData.serviceTypeId, serviceTypes, prefilledTime, prefilledDate]);

  // Fetch available slots when date and service are selected
  useEffect(() => {
    if (selectedDate && formData.serviceTypeId && selectedServiceType) {
      fetchAvailableSlots();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [selectedDate, formData.serviceTypeId, selectedServiceType]);

  // Fetch team members when date, time, and service are selected.
  useEffect(() => {
    if (formData.startTime && formData.endTime && formData.serviceTypeId) {
      fetchAvailableTeamMembers();
    } else {
      // Clear available team members if time/service not selected
      setAvailableTeamMembers([]);
      setSelectedTeamMembers([]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [formData.startTime, formData.endTime, formData.serviceTypeId]);

  // Keep selected team members in sync with the active staff list, preserving calendar preselection.
  useEffect(() => {
    if (availableTeamMembers.length === 0) {
      if (!prefilledStaffMember && selectedTeamMembers.length > 0) {
        setSelectedTeamMembers([]);
      }
    } else if (selectedTeamMembers.length > 0) {
      const selectableIds = availableTeamMembers
        .filter((member) => member.available)
        .map((member) => member.id);
      const validSelections = selectedTeamMembers.filter((id) => selectableIds.includes(id));
      if (validSelections.length !== selectedTeamMembers.length) {
        setSelectedTeamMembers(validSelections);
      }
    }
  }, [availableTeamMembers, prefilledStaffMember, selectedTeamMembers]);

  // Note: Availability is resolved via the unified-bookings availability API.

  const fetchServiceTypes = async () => {
    if (!businessId) {
      setServiceTypes([]);
      return;
    }

    try {
      const response = await api.get(`/api/service-types/business/${businessId}/public`);
      setServiceTypes(response.data.serviceTypes);
    } catch (error) {
      setError('Failed to load service types');
    }
  };

  const fetchAvailableSlots = async () => {
    setLoadingSlots(true);
    setError('');
    try {
      const serviceType = selectedServiceType || serviceTypes.find(st => st._id === formData.serviceTypeId);
      const slotDuration = serviceType?.duration || 30;
      const selectedLocalDate = parseLocalDateString(selectedDate);
      if (!selectedLocalDate) {
        setError('Invalid selected date');
        return;
      }
      const dateParam = `${selectedLocalDate.getFullYear()}-${String(selectedLocalDate.getMonth() + 1).padStart(2, '0')}-${String(selectedLocalDate.getDate()).padStart(2, '0')}`;
      const response = await api.get(
        `/api/opening-hours/${formData.businessId}/availability/${dateParam}`,
        { params: { duration: slotDuration } }
      );

      const slots = (response.data.availableSlots || []).map(slot => ({
        startTime: slot.startTime,
        endTime: slot.endTime,
        availableTeamMembers: slot.availableTeamMembers || []
      }));

      setAvailableSlots(slots);
    } catch (error) {
      setError('Failed to load available time slots');
    } finally {
      setLoadingSlots(false);
    }
  };

  const fetchAvailableTeamMembers = async () => {
    if (!formData.startTime || !formData.endTime || !formData.serviceTypeId) {
      setAvailableTeamMembers([]);
      if (!prefilledStaffMember) {
        setSelectedTeamMembers([]);
      }
      return;
    }

    try {
      const response = await api.get(
        `/api/unified-bookings/available-team-members/${formData.serviceTypeId}`,
        {
          params: {
            startTime: formData.startTime,
            endTime: formData.endTime,
            businessId: formData.businessId,
            forAssignment: 'true'
          }
        }
      );

      const formattedMembers = (response.data.teamMembers || response.data.availableTeamMembers || []).map((member) => ({
        id: normaliseId(member.staffMemberId || member.id),
        businessMemberId: normaliseId(member.id),
        name: member.name,
        role: member.role || 'Staff',
        available: member.available !== false,
        availabilityReason: member.availability?.reason || (member.available === false ? 'Unavailable' : 'Available')
      })).filter((member) => Boolean(member.id));

      setAvailableTeamMembers(formattedMembers);

      const selectableMembers = formattedMembers.filter((member) => member.available);

      if (prefilledStaffMember) {
        const mappedPrefilledMember = selectableMembers.find(member =>
          normaliseId(member.id) === normaliseId(prefilledStaffMember) ||
          normaliseId(member.businessMemberId) === normaliseId(prefilledStaffMember)
        );
        setSelectedTeamMembers(mappedPrefilledMember ? [mappedPrefilledMember.id] : []);
      } else if (selectableMembers.length === 1) {
        setSelectedTeamMembers([selectableMembers[0].id]);
      } else {
        setSelectedTeamMembers([]);
      }
    } catch (error) {
      setError('Failed to load available team members');
      setAvailableTeamMembers([]);
      if (!prefilledStaffMember) {
        setSelectedTeamMembers([]);
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLeadSelect = (lead) => {
    if (!lead) {
      setFormData(prev => ({ ...prev, selectedLeadId: '', clientName: '', clientEmail: '', clientPhone: '' }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      selectedLeadId: lead._id,
      clientName: lead.name || '',
      clientEmail: lead.email || '',
      clientPhone: lead.phone || ''
    }));
  };

  const handleLocationChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      location: {
        ...prev.location,
        [field]: value
      }
    }));
  };

  const generateTimeSlots = (serviceType) => {
    if (!serviceType || !selectedDate) return [];

    // If no available slots, return empty array
    if (availableSlots.length === 0) return [];

    const selectedDateObj = parseLocalDateString(selectedDate);
    if (!selectedDateObj) return [];

    const daySlots = availableSlots.filter(slot => {
      const slotDate = new Date(slot.startTime);
      slotDate.setHours(0, 0, 0, 0);
      return slotDate.getTime() === selectedDateObj.getTime();
    });

    const now = new Date();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const isToday = selectedDateObj.getTime() === today.getTime();

    // Convert to the format expected by the UI
    return daySlots
      .map(slot => ({
        startTime: new Date(slot.startTime),
        endTime: new Date(slot.endTime),
        label: `${new Date(slot.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${new Date(slot.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
        availableTeamMembers: slot.availableTeamMembers || []
      }))
      .filter(slot => !isToday || slot.startTime > now);
  };

  // Manual bookings can use any start time; opening-hours slots are suggestions only.
  const applyCustomStartTime = (timeValue, dateValue = selectedDate) => {
    if (!dateValue || !selectedServiceType || !timeValue) return false;
    const timePart = timeValue.length === 5 ? `${timeValue}:00` : timeValue;
    const start = new Date(`${dateValue}T${timePart}`);
    if (Number.isNaN(start.getTime())) return false;

    const duration = selectedServiceType.duration || 60;
    const end = new Date(start);
    end.setMinutes(end.getMinutes() + duration);

    setFormData((prev) => ({
      ...prev,
      startTime: start.toISOString(),
      endTime: end.toISOString()
    }));
    return true;
  };

  // Keep booking start/end in sync with the free-form time picker for manual overrides.
  useEffect(() => {
    if (!selectedDate || !selectedServiceType || !customStartTime) return;
    applyCustomStartTime(customStartTime, selectedDate);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [selectedDate, selectedServiceType, customStartTime]);

  const handleNext = () => {
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      // Create a temporary user for the client if they don't exist
      let clientId = user._id; // Default to current user for now

      // Manual bookings use the supplier as the booking client record; couple details are in manualBookingDetails.
      // Allow empty team members for "force booking" functionality
      let teamMemberIdsToSend = [];
      let validSelectedTeamMembers = [];
      let selectedMemberDetails = [];

      if (selectedTeamMembers.length > 0) {
        validSelectedTeamMembers = selectedTeamMembers.filter(id => {
          const selectedId = id?.toString ? id.toString() : id;
          const exists = availableTeamMembers.some(m => {
            const memberId = m.id?.toString ? m.id.toString() : m.id;
            return memberId === selectedId;
          });
          return exists;
        });
        
        if (validSelectedTeamMembers.length === 0 && selectedTeamMembers.length > 0) {
          setError('Selected team members could not be found. Please select again.');
          return;
        }
        
        selectedMemberDetails = validSelectedTeamMembers.map(id => {
          const member = availableTeamMembers.find(m => {
            const memberId = m.id?.toString ? m.id.toString() : m.id;
            const selectedId = id?.toString ? id.toString() : id;
            return memberId === selectedId;
          });
          return {
            id: member?.id || id,
            name: member?.name || 'Unknown',
            originalId: id
          };
        });
        
        teamMemberIdsToSend = selectedMemberDetails.map(detail => {
          const member = availableTeamMembers.find(m => {
            const memberId = m.id?.toString ? m.id.toString() : m.id;
            const detailId = detail.id?.toString ? detail.id.toString() : detail.id;
            return memberId === detailId;
          });
          return (member?.id?.toString ? member.id.toString() : member?.id) || detail.id;
        });
      }

      const bookingData = {
        ...formData,
        client: clientId,
        manualAssignedTeamMembers: teamMemberIdsToSend,
        isManualBooking: true, // Flag to indicate this is a manual booking
        leadId: formData.selectedLeadId || undefined,
        supplierNotes: `Manual booking created by ${user.firstName} ${user.lastName}. Client: ${formData.clientName} (${formData.clientEmail})`
      };

      const membersWithoutShift = await getSelectedMembersWithoutShift(teamMemberIdsToSend);
      if (membersWithoutShift.length > 0) {
        const startTimeInput = formatTimeInputValue(formData.startTime) || customStartTime || '09:00';
        const endTimeInput = formatTimeInputValue(formData.endTime)
          || formatTimeInputValue(new Date(new Date(formData.startTime).getTime() + (60 * 60 * 1000)));
        setShiftStartTimeInput(startTimeInput);
        setShiftEndTimeInput(endTimeInput);
        setShiftPromptState({
          bookingData,
          selectedMemberIds: teamMemberIdsToSend,
          membersWithoutShift
        });
        setShiftPromptOpen(true);
        return;
      }

      await proceedWithBookingSubmission(bookingData, teamMemberIdsToSend);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const submitManualBooking = async (bookingPayload) => {
    const response = await api.post('/api/unified-bookings/manual', bookingPayload);
    if (onBookingSuccess) {
      onBookingSuccess(response.data);
    }
    return response;
  };

  const proceedWithBookingSubmission = async (bookingData, selectedMemberIds = []) => {
    const conflictCheckResult = await checkTeamMemberBookingConflicts(selectedMemberIds);
    if (conflictCheckResult.hasConflict) {
      setConflictResolutionState({
        bookingData,
        selectedMemberIds,
        conflictingMemberIds: conflictCheckResult.conflictingMemberIds,
        conflictingMembers: conflictCheckResult.conflictingMembers,
        alternativeMembers: conflictCheckResult.alternativeMembers
      });
      setReplacementTeamMemberId('');
      setConflictDialogOpen(true);
      return false;
    }

    await submitManualBooking(bookingData);
    setSuccess(true);
    return true;
  };

  const getBookingAssignmentIds = (assignment) => {
    if (!assignment) return [];
    return [
      normaliseId(assignment.member?._id || assignment.member),
      normaliseId(assignment.staffMember?._id || assignment.staffMember)
    ].filter(Boolean);
  };

  const checkTeamMemberBookingConflicts = async (selectedMemberIds) => {
    if (!selectedMemberIds || selectedMemberIds.length === 0 || !formData.startTime || !formData.endTime) {
      return { hasConflict: false, conflictingMemberIds: [], conflictingMembers: [], alternativeMembers: [] };
    }

    const response = await api.get('/api/unified-bookings', {
      params: {
        type: 'supplier',
        startDate: formData.startTime,
        endDate: formData.endTime,
        businessId: formData.businessId
      }
    });

    const bookings = response.data?.bookings || [];
    const selectedIdSet = new Set(selectedMemberIds.map(normaliseId).filter(Boolean));

    const memberHasConflict = (member) => {
      const memberIds = new Set([
        normaliseId(member?.id),
        normaliseId(member?.businessMemberId)
      ].filter(Boolean));
      if (memberIds.size === 0) return false;

      return bookings.some((booking) => {
        if (!booking?.startTime || !booking?.endTime) return false;
        if (booking.status === 'cancelled' || booking.status === 'rejected') return false;

        const bookingStart = new Date(booking.startTime);
        const bookingEnd = new Date(booking.endTime);
        const requestedStart = new Date(formData.startTime);
        const requestedEnd = new Date(formData.endTime);
        const overlaps = bookingStart < requestedEnd && bookingEnd > requestedStart;
        if (!overlaps) return false;

        return (booking.assignedTeamMembers || []).some((assignment) => {
          const assignmentIds = getBookingAssignmentIds(assignment);
          return assignmentIds.some((id) => memberIds.has(id));
        });
      });
    };

    const conflictingMembers = availableTeamMembers.filter((member) => {
      const memberId = normaliseId(member.id);
      if (!selectedIdSet.has(memberId)) return false;
      return memberHasConflict(member);
    });

    const conflictingMemberIds = conflictingMembers.map((member) => normaliseId(member.id)).filter(Boolean);
    if (conflictingMemberIds.length === 0) {
      return { hasConflict: false, conflictingMemberIds: [], conflictingMembers: [], alternativeMembers: [] };
    }

    const alternativeMembers = availableTeamMembers.filter((member) => {
      const memberId = normaliseId(member.id);
      if (selectedIdSet.has(memberId)) return false;
      return member.available && !memberHasConflict(member);
    });

    return {
      hasConflict: true,
      conflictingMemberIds,
      conflictingMembers,
      alternativeMembers
    };
  };

  const getSelectedMembersWithoutShift = async (selectedMemberIds) => {
    if (!selectedMemberIds || selectedMemberIds.length === 0 || !formData.startTime || !formData.endTime || !formData.businessId) {
      return [];
    }

    const response = await api.get(`/api/team-member-shifts/business/${formData.businessId}`, {
      params: {
        start: formData.startTime,
        end: formData.endTime
      }
    });

    const shifts = response.data?.shifts || [];
    const staffIdsWithShift = new Set(
      shifts
        .map((shift) => normaliseId(shift.teamMember?._id || shift.teamMember))
        .filter(Boolean)
    );
    const selectedIdSet = new Set(selectedMemberIds.map(normaliseId).filter(Boolean));

    return availableTeamMembers.filter((member) => {
      const memberId = normaliseId(member.id);
      if (!memberId || !selectedIdSet.has(memberId)) return false;
      return !staffIdsWithShift.has(memberId);
    });
  };

  const handleContinueAnyway = async () => {
    if (!conflictResolutionState) return;
    setLoading(true);
    setError('');
    try {
      await submitManualBooking(conflictResolutionState.bookingData);
      setConflictDialogOpen(false);
      setConflictResolutionState(null);
      setSuccess(true);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveUnassigned = async () => {
    if (!conflictResolutionState) return;
    setLoading(true);
    setError('');
    try {
      const unassignedPayload = {
        ...conflictResolutionState.bookingData,
        manualAssignedTeamMembers: []
      };
      setSelectedTeamMembers([]);
      await submitManualBooking(unassignedPayload);
      setConflictDialogOpen(false);
      setConflictResolutionState(null);
      setSuccess(true);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignToAnother = async () => {
    if (!conflictResolutionState || !replacementTeamMemberId) return;
    setLoading(true);
    setError('');
    try {
      const nonConflictingSelected = (conflictResolutionState.selectedMemberIds || []).filter(
        (id) => !conflictResolutionState.conflictingMemberIds.includes(normaliseId(id))
      );
      const updatedSelections = [...new Set([...nonConflictingSelected, replacementTeamMemberId])];
      const updatedPayload = {
        ...conflictResolutionState.bookingData,
        manualAssignedTeamMembers: updatedSelections
      };
      setSelectedTeamMembers(updatedSelections);
      await submitManualBooking(updatedPayload);
      setConflictDialogOpen(false);
      setConflictResolutionState(null);
      setReplacementTeamMemberId('');
      setSuccess(true);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const handleAddShiftAndContinue = async () => {
    if (!shiftPromptState) return;
    setLoading(true);
    setError('');
    try {
      if (!shiftStartTimeInput || !shiftEndTimeInput) {
        setError('Please choose a shift start and end time.');
        setLoading(false);
        return;
      }

      const shiftDateValue = selectedDate || (formData.startTime ? new Date(formData.startTime).toISOString().split('T')[0] : '');
      if (!shiftDateValue) {
        setError('Please select a booking date before adding a shift.');
        setLoading(false);
        return;
      }

      const shiftStartDateTime = new Date(`${shiftDateValue}T${shiftStartTimeInput}:00`);
      const shiftEndDateTime = new Date(`${shiftDateValue}T${shiftEndTimeInput}:00`);

      if (
        Number.isNaN(shiftStartDateTime.getTime())
        || Number.isNaN(shiftEndDateTime.getTime())
      ) {
        setError('Please choose valid shift times.');
        setLoading(false);
        return;
      }

      if (shiftEndDateTime <= shiftStartDateTime) {
        setError('Shift end time must be after shift start time.');
        setLoading(false);
        return;
      }

      const membersWithoutShift = shiftPromptState.membersWithoutShift || [];
      await Promise.all(membersWithoutShift.map((member) => (
        api.post('/api/team-member-shifts', {
          teamMemberId: member.id,
          startTime: shiftStartDateTime.toISOString(),
          endTime: shiftEndDateTime.toISOString()
        })
      )));

      setShiftPromptOpen(false);
      setShiftPromptState(null);
      setShiftStartTimeInput('');
      setShiftEndTimeInput('');
      await proceedWithBookingSubmission(shiftPromptState.bookingData, shiftPromptState.selectedMemberIds || []);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to add shift for this staff member.');
    } finally {
      setLoading(false);
    }
  };

  const handleContinueWithoutShift = async () => {
    if (!shiftPromptState) return;
    setLoading(true);
    setError('');
    try {
      setShiftPromptOpen(false);
      setShiftPromptState(null);
      setShiftStartTimeInput('');
      setShiftEndTimeInput('');
      await proceedWithBookingSubmission(shiftPromptState.bookingData, shiftPromptState.selectedMemberIds || []);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      businessId,
      serviceTypeId: '',
      startTime: '',
      endTime: '',
      clientNotes: '',
      clientName: '',
      clientEmail: '',
      clientPhone: '',
      selectedLeadId: '',
      location: {
        type: 'on_site',
        address: '',
        videoLink: ''
      }
    });
    setActiveStep(0);
    setError('');
    setSuccess(false);
  };

  if (success) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box textAlign="center">
          <CheckCircle size={64} color="#4caf50" />
          <Typography variant="h5" gutterBottom sx={{ mt: 2 }}>
            Booking Created Successfully!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            The manual booking has been added to your calendar.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="outlined" onClick={resetForm}>
              Create Another Booking
            </Button>
            <Button variant="contained" onClick={() => {
              if (onClose) {
                onClose();
              } else {
                // Navigate to bookings page
                window.location.href = '/supplier/bookings';
              }
            }}>
              View All Bookings
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  if (!businessId) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="warning">
          <Typography variant="h6">No Business Selected</Typography>
          <Typography variant="body2">
            Please select a business from the supplier dashboard to create manual bookings.
          </Typography>
        </Alert>
      </Container>
    );
  }

  const renderServiceSelection = () => (
    <Box sx={{ 
      p: { xs: 2, md: 3 },
      bgcolor: (muiTheme) => alpha(muiTheme.palette.common.white, 0.72),
      backdropFilter: 'blur(10px)',
      borderRadius: 1
    }}>
      <Typography variant="h6" gutterBottom sx={{ pb: 1 }}>
        Select Appointment
      </Typography>
      
      <Grid container spacing={2}>
        {serviceTypes.filter(isServiceAllowedForPrefilledStaff).map((serviceType) => (
          <Grid item xs={12} sm={6} md={4} key={serviceType._id}>
            <Card
              sx={{
                cursor: 'pointer',
                position: 'relative',
                border: formData.serviceTypeId === serviceType._id ? 2 : 1,
                borderColor: formData.serviceTypeId === serviceType._id ? 'primary.main' : 'divider',
                backgroundColor: '#fff',
                borderRadius: 2,
                boxShadow: formData.serviceTypeId === serviceType._id ? 2 : 0,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: '#fff',
                  boxShadow: 2
                },
                transition: 'all 0.2s ease-in-out'
              }}
              onClick={() => handleInputChange('serviceTypeId', serviceType._id)}
            >
              {formData.serviceTypeId === serviceType._id && (
                <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1 }}>
                  <CheckCircle size={22} weight="fill" color="#ec4899" />
                </Box>
              )}
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {serviceType.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {serviceType.description}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Clock size={16} />
                  <Typography variant="body2">
                    {serviceType.duration} minutes
                  </Typography>
                </Box>
                {serviceType.isBlockerService && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="warning.main">
                      Blocker Service
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
        {serviceTypes.filter(isServiceAllowedForPrefilledStaff).length === 0 && (
          <Grid item xs={12}>
            <Alert severity="info">
              No appointment types are available for this staff member.
            </Alert>
          </Grid>
        )}
      </Grid>

      <Box sx={{ mt: { xs: 2, md: 3 }, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          size={isMobile ? 'medium' : 'large'}
          onClick={handleNext}
          disabled={!formData.serviceTypeId}
          endIcon={<CaretRight size={20} />}
        >
          Next
        </Button>
      </Box>
    </Box>
  );

  const renderTeamMemberSelection = () => (
    <Box sx={{ 
      p: { xs: 2, md: 3 },
      bgcolor: (muiTheme) => alpha(muiTheme.palette.common.white, 0.72),
      backdropFilter: 'blur(10px)',
      borderRadius: 1
    }}>
      <Typography variant="h6" gutterBottom>
        Select Team Members
      </Typography>
      
      {availableTeamMembers.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body1" gutterBottom>
            No team members are set up for this appointment type. You can still create the appointment and assign someone later.
          </Typography>
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {availableTeamMembers.map((member) => {
            const isSelected = selectedTeamMembers.includes(member.id);
            const isUnavailable = !member.available;
            return (
              <Grid item xs={12} sm={6} md={4} key={member.id}>
                <Card
                  sx={{
                    cursor: isUnavailable ? 'not-allowed' : 'pointer',
                    position: 'relative',
                    opacity: isUnavailable ? 0.72 : 1,
                    border: isSelected ? 2 : 1,
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    backgroundColor: isUnavailable ? 'grey.50' : '#fff',
                    borderRadius: 2,
                    boxShadow: isSelected ? 2 : 0,
                    '&:hover': isUnavailable ? {} : { 
                      borderColor: 'primary.main',
                      backgroundColor: '#fff',
                      boxShadow: 2
                    },
                    transition: 'all 0.2s ease-in-out'
                  }}
                  onClick={() => {
                    if (isUnavailable) return;
                    if (isSelected) {
                      setSelectedTeamMembers(prev => prev.filter(id => id !== member.id));
                    } else {
                      setSelectedTeamMembers(prev => [...prev, member.id]);
                    }
                  }}
                >
                  {isSelected && (
                    <Box sx={{ position: 'absolute', top: 10, right: 10, zIndex: 1 }}>
                      <CheckCircle size={22} weight="fill" color="#ec4899" />
                    </Box>
                  )}
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {member.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {member.role}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      {isUnavailable ? (
                        <XCircle size={16} color="#d32f2f" />
                      ) : (
                        <CheckCircle 
                          size={16} 
                          color={isSelected ? '#ec4899' : '#2e7d32'} 
                        />
                      )}
                      <Typography
                        variant="body2"
                        fontWeight={isSelected ? 'bold' : 'normal'}
                        color={isUnavailable ? 'error.main' : 'text.primary'}
                      >
                        {isSelected ? 'Selected' : isUnavailable ? 'Unavailable' : 'Available'}
                      </Typography>
                    </Box>
                    {isUnavailable && member.availabilityReason && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {member.availabilityReason}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Box sx={{ mt: { xs: 2, md: 3 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Button
          variant="outlined"
          size={isMobile ? 'small' : 'medium'}
          onClick={handleBack}
          startIcon={<CaretLeft size={20} />}
        >
          Back
        </Button>
        {availableTeamMembers.length === 0 ? (
          <Button
            variant="contained"
            size={isMobile ? 'small' : 'medium'}
            onClick={() => {
              setSelectedTeamMembers([]);
              handleNext();
            }}
            sx={{ bgcolor: 'grey.900', color: 'white', '&:hover': { bgcolor: 'grey.800' } }}
          >
            Continue Without Assignment
          </Button>
        ) : (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              onClick={() => {
                setSelectedTeamMembers([]);
                handleNext();
              }}
            >
              Assign Later
            </Button>
            <Button
              variant="contained"
              size={isMobile ? 'small' : 'medium'}
              onClick={handleNext}
              endIcon={<CaretRight size={20} />}
            >
              Next
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );

  const renderDateTimeSelection = () => (
    <Box sx={{ 
      p: { xs: 2, md: 3 },
      bgcolor: (muiTheme) => alpha(muiTheme.palette.common.white, 0.72),
      backdropFilter: 'blur(10px)',
      borderRadius: 1
    }}>
      <Typography variant="h6" gutterBottom>
        Choose Date & Time
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            label="Date"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: new Date().toISOString().split('T')[0] }}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <Box sx={{ 
            maxHeight: 300, 
            overflow: 'auto',
            p: 2,
            bgcolor: (muiTheme) => alpha(muiTheme.palette.common.white, 0.6),
            borderRadius: 1
          }}>
            {selectedDate && selectedServiceType ? (
              (() => {
                const timeSlots = loadingSlots ? [] : generateTimeSlots(selectedServiceType);

                return (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      You can book any time here. Couples can only choose times within your opening hours.
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
                      <TextField
                        type="time"
                        size="small"
                        label="Start time"
                        value={customStartTime}
                        onChange={(e) => {
                          const nextTime = e.target.value;
                          setCustomStartTime(nextTime);
                          applyCustomStartTime(nextTime);
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ step: 300 }}
                        sx={{ width: 160 }}
                      />
                      {formData.startTime && formData.endTime && (
                        <Typography variant="body2" color="text.secondary">
                          Ends {new Date(formData.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      )}
                    </Box>

                    {loadingSlots ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          Loading suggested times within opening hours...
                        </Typography>
                      </Box>
                    ) : timeSlots.length > 0 ? (
                      <>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          Suggested times (within opening hours)
                        </Typography>
                        <Grid container spacing={1}>
                          {timeSlots.map((slot, index) => (
                            <Grid item xs={6} sm={4} key={index}>
                              <Button
                                variant={formData.startTime === slot.startTime.toISOString() ? 'contained' : 'outlined'}
                                onClick={() => {
                                  const startTime = slot.startTime.toISOString();
                                  const duration = selectedServiceType?.duration || 60;
                                  const calculatedEndTime = new Date(slot.startTime);
                                  calculatedEndTime.setMinutes(calculatedEndTime.getMinutes() + duration);
                                  const hours = String(slot.startTime.getHours()).padStart(2, '0');
                                  const minutes = String(slot.startTime.getMinutes()).padStart(2, '0');
                                  setCustomStartTime(`${hours}:${minutes}`);
                                  handleInputChange('startTime', startTime);
                                  handleInputChange('endTime', calculatedEndTime.toISOString());
                                }}
                                sx={{
                                  width: '100%',
                                  fontSize: '0.75rem',
                                  py: 1
                                }}
                              >
                                {slot.label}
                              </Button>
                            </Grid>
                          ))}
                        </Grid>
                      </>
                    ) : (
                      <Alert severity="info">
                        <Typography variant="body2">
                          No suggested times within opening hours for this date and appointment length.
                          Enter any start time above to continue.
                        </Typography>
                      </Alert>
                    )}
                  </Box>
                );
              })()
            ) : !selectedServiceType ? (
              <Typography variant="body2" color="text.secondary">
                Please select a service first to choose a time
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Please select a date to choose a time
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      <Box sx={{ mt: { xs: 2, md: 3 }, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Button size={isMobile ? 'small' : 'medium'} onClick={handleBack} startIcon={<CaretLeft size={20} />}>
          Back
        </Button>
        <Button
          variant="contained"
          size={isMobile ? 'medium' : 'large'}
          onClick={handleNext}
          disabled={!formData.startTime || !formData.endTime}
          endIcon={<CaretRight size={20} />}
        >
          Next
        </Button>
      </Box>
    </Box>
  );

  const renderClientDetails = () => (
    <Box sx={{ 
      p: { xs: 2, md: 3 },
      bgcolor: (muiTheme) => alpha(muiTheme.palette.common.white, 0.72),
      backdropFilter: 'blur(10px)',
      borderRadius: 1
    }}>
      <Typography variant="h6" gutterBottom>
        Client Details
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <LeadCoupleAutocomplete
            businessId={businessId}
            value={formData.selectedLeadId || ''}
            onLeadChange={handleLeadSelect}
            label="Select couple"
            allowNone
            helperText="Selecting a couple syncs this booking to them and fills in their details."
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Client Name"
            value={formData.clientName}
            onChange={(e) => handleInputChange('clientName', e.target.value)}
            required
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Client Email"
            type="email"
            value={formData.clientEmail}
            onChange={(e) => handleInputChange('clientEmail', e.target.value)}
            required
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Client Phone"
            value={formData.clientPhone}
            onChange={(e) => handleInputChange('clientPhone', e.target.value)}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Booking Notes"
            multiline
            rows={3}
            value={formData.clientNotes}
            onChange={(e) => handleInputChange('clientNotes', e.target.value)}
            placeholder="Add any notes about this booking..."
          />
        </Grid>

        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Location Type</InputLabel>
            <Select
              value={formData.location.type}
              onChange={(e) => handleLocationChange('type', e.target.value)}
            >
              <MenuItem value="on_site">On-site</MenuItem>
              <MenuItem value="off_site">Off-site</MenuItem>
              <MenuItem value="virtual">Virtual</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {(formData.location.type === 'on_site' || formData.location.type === 'off_site') && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Location Address"
              value={formData.location.address}
              onChange={(e) => handleLocationChange('address', e.target.value)}
              placeholder={formData.location.type === 'on_site' 
                ? "Enter your business address..." 
                : "Enter the event location address..."
              }
            />
          </Grid>
        )}

        {formData.location.type === 'virtual' && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Video Call Link"
              value={formData.location.videoLink}
              onChange={(e) => handleLocationChange('videoLink', e.target.value)}
              placeholder="Enter video call link..."
            />
          </Grid>
        )}
      </Grid>

      <Box sx={{ mt: { xs: 2, md: 3 }, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Button size={isMobile ? 'small' : 'medium'} onClick={handleBack} startIcon={<CaretLeft size={20} />}>
          Back
        </Button>
        <Button
          variant="contained"
          size={isMobile ? 'medium' : 'large'}
          onClick={handleNext}
          disabled={!formData.clientName || !formData.clientEmail}
          endIcon={<CaretRight size={20} />}
        >
          Next
        </Button>
      </Box>
    </Box>
  );

  const renderConfirmation = () => (
    <Box sx={{ 
      p: { xs: 2, md: 3 },
      bgcolor: (muiTheme) => alpha(muiTheme.palette.common.white, 0.72),
      backdropFilter: 'blur(10px)',
      borderRadius: 1
    }}>
      <Typography variant="h6" gutterBottom>
        Confirm Manual Booking
      </Typography>

      <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light' }}>
        <Typography variant="body2" color="info.dark">
          <strong>Manual booking:</strong> This adds the appointment to your calendar and sends a confirmation email to the couple. Assigned team members are notified too.
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" gutterBottom>Service</Typography>
          <Typography variant="body1">{selectedServiceType?.name}</Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" gutterBottom>Date & Time</Typography>
          <Typography variant="body1">
            {new Date(formData.startTime).toLocaleDateString()} at{' '}
            {new Date(formData.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" gutterBottom>Client</Typography>
          <Typography variant="body1">{formData.clientName}</Typography>
          <Typography variant="body2" color="text.secondary">{formData.clientEmail}</Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" gutterBottom>Location</Typography>
          <Typography variant="body1">
            {formData.location.type === 'on_site' && (formData.location.address || 'Your business location')}
            {formData.location.type === 'off_site' && (formData.location.address || 'Event location')}
            {formData.location.type === 'virtual' && 'Video Call'}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>Assigned Team Members</Typography>
          {selectedTeamMembers.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedTeamMembers.map((memberId) => {
                const member = availableTeamMembers.find(m => m.id === memberId);
                return member ? (
                  <Chip
                    key={memberId}
                    label={member.name}
                    color="primary"
                    variant="outlined"
                  />
                ) : null;
              })}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No team members assigned
            </Typography>
          )}
        </Grid>

        {formData.clientNotes && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>Notes</Typography>
            <Typography variant="body1">{formData.clientNotes}</Typography>
          </Grid>
        )}
      </Grid>

      <Box sx={{ mt: { xs: 2, md: 3 }, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Button size={isMobile ? 'small' : 'medium'} onClick={handleBack} startIcon={<CaretLeft size={20} />}>
          Back
        </Button>
        <Button
          variant="contained"
          size={isMobile ? 'medium' : 'large'}
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircle size={20} />}
        >
          {loading ? 'Creating Booking...' : 'Create Manual Booking'}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 4 }, px: { xs: 1.5, sm: 2 } }}>
      <Box sx={{ mb: { xs: 2, md: 3 }, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="outlined"
          size={isMobile ? 'small' : 'medium'}
          startIcon={<CaretLeft size={20} />}
          onClick={() => navigate('/supplier/bookings', { replace: true })}
        >
          Back to Bookings
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: { xs: 2, md: 3 } }}>
          {error}
        </Alert>
      )}

      <Typography variant={isMobile ? 'h5' : 'h4'} gutterBottom align="center" sx={{ mb: 1 }}>
        Add Manual Booking
      </Typography>

      {isMobile ? (
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Chip
            label={`Step ${activeStep + 1} of ${steps.length}: ${steps[activeStep]}`}
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
        </Box>
      ) : (
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      {activeStep === 0 && renderServiceSelection()}
      {activeStep === 1 && renderDateTimeSelection()}
      {activeStep === 2 && renderTeamMemberSelection()}
      {activeStep === 3 && renderClientDetails()}
      {activeStep === 4 && renderConfirmation()}

      <Dialog
        open={conflictDialogOpen}
        onClose={() => !loading && setConflictDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Team member conflict</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This staff member already has an appointment scheduled in this time window. What would you like to do?
          </Typography>
          {conflictResolutionState?.conflictingMembers?.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Conflicting staff:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {conflictResolutionState.conflictingMembers.map((member) => (
                  <Chip key={member.id} label={member.name} color="warning" variant="outlined" size="small" />
                ))}
              </Box>
            </Box>
          )}
          {(conflictResolutionState?.alternativeMembers?.length || 0) > 0 && (
            <FormControl fullWidth>
              <InputLabel>Assign to another available staff member</InputLabel>
              <Select
                label="Assign to another available staff member"
                value={replacementTeamMemberId}
                onChange={(e) => setReplacementTeamMemberId(e.target.value)}
              >
                {conflictResolutionState.alternativeMembers.map((member) => (
                  <MenuItem key={member.id} value={member.id}>
                    {member.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, justifyContent: 'space-between' }}>
          <Button onClick={handleLeaveUnassigned} disabled={loading} variant="outlined">
            Leave as unassigned
          </Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {(conflictResolutionState?.alternativeMembers?.length || 0) > 0 && (
              <Button
                onClick={handleAssignToAnother}
                disabled={loading || !replacementTeamMemberId}
                variant="contained"
              >
                Assign to another
              </Button>
            )}
            <Button onClick={handleContinueAnyway} disabled={loading} color="warning" variant="contained">
              Continue anyway
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={shiftPromptOpen}
        onClose={() => {
          if (loading) return;
          setShiftPromptOpen(false);
          setShiftStartTimeInput('');
          setShiftEndTimeInput('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add shift for assigned staff?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            This staff member does not have a shift covering the selected booking time. Choose when their shift should start and end on this day.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="time"
                label="Shift start time"
                value={shiftStartTimeInput}
                onChange={(e) => setShiftStartTimeInput(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="time"
                label="Shift end time"
                value={shiftEndTimeInput}
                onChange={(e) => setShiftEndTimeInput(e.target.value)}
                InputLabelProps={{ shrink: true }}
                disabled={loading}
              />
            </Grid>
          </Grid>
          {(shiftPromptState?.membersWithoutShift?.length || 0) > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {shiftPromptState.membersWithoutShift.map((member) => (
                <Chip key={member.id} label={member.name} color="warning" variant="outlined" size="small" />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleContinueWithoutShift} disabled={loading} variant="outlined">
            Continue anyway
          </Button>
          <Button onClick={handleAddShiftAndContinue} disabled={loading} variant="contained">
            Add shift and continue
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SupplierManualBookingForm;
